import invariant from "tiny-invariant";

import { CommodityRatesToCommodity } from "@/generated/api/prices";
import { CommodityOutlookQueryResult } from "@/routes/stock-exchange/stock-exchange.api";
import { Range } from "@/routes/stock-exchange/stock-exchange.utils";

import { FeatureMode } from "../feature-mode.service";
import {
  DummyRatesPeriod,
  isMaskableCommodity,
  MaskableCommodity,
} from "./stock-exchange.dummy";
import { FullStrategy } from "./strategies/full.strategy";
import { TrialStrategy } from "./strategies/trial.strategy";

type Mode = Exclude<FeatureMode, "not-available">;

interface Strategy {
  isMasked: (commodity: CommodityRatesToCommodity, range: Range) => boolean;
  getDummyRates: (
    commodity: MaskableCommodity,
    range: Range,
  ) => DummyRatesPeriod[] | undefined;
  getDummyOutlook: (
    commodity: MaskableCommodity,
  ) => CommodityOutlookQueryResult | undefined;
  showCallToAction: () => boolean;
}

/**
 * MATIF charts are blurred for users with trial version of the feature.
 */
class StockExchangeFlow {
  private currentStrategy: Strategy | undefined;
  private strategies: Map<Mode, Strategy>;

  constructor() {
    this.strategies = new Map<Mode, Strategy>([
      ["trial", new TrialStrategy()],
      ["full", new FullStrategy()],
    ]);
  }

  public setStrategy(mode: Mode) {
    const strategy = this.strategies.get(mode);
    // eslint-disable-next-line lingui/no-unlocalized-strings
    invariant(strategy, `Strategy for mode ${mode} does not exist`);
    this.currentStrategy = strategy;
  }

  public getMasked(
    commodity: CommodityRatesToCommodity,
    range: Range,
  ): boolean {
    // eslint-disable-next-line lingui/no-unlocalized-strings
    invariant(this.currentStrategy, "No current strategy available");
    return this.currentStrategy.isMasked(commodity, range);
  }

  public getDummyRates(commodity: MaskableCommodity, range: Range) {
    // eslint-disable-next-line lingui/no-unlocalized-strings
    invariant(this.currentStrategy, "No current strategy available");
    return this.currentStrategy.getDummyRates(commodity, range);
  }

  public getDummyOutlook(commodity: MaskableCommodity) {
    // eslint-disable-next-line lingui/no-unlocalized-strings
    invariant(this.currentStrategy, "No current strategy available");
    return this.currentStrategy.getDummyOutlook(commodity);
  }

  public showCallToAction(): boolean {
    // eslint-disable-next-line lingui/no-unlocalized-strings
    invariant(this.currentStrategy, "No current strategy available");
    return this.currentStrategy.showCallToAction();
  }
}

export { isMaskableCommodity, StockExchangeFlow };
export type { MaskableCommodity, Mode, Strategy };
